'use client';

import { useState } from 'react';
import { motion } from 'framer-motion';
import Image from 'next/image';
import Link from 'next/link';
import blogs from '../../../../data/blogs';

export default function BlogFeatured() {
  const [loading, setLoading] = useState(true);

  // Get the most recent blog (highest sort value)
  const featuredBlog = [...blogs].sort((a, b) => b.sort - a.sort)[0];

  if (!featuredBlog) return null;

  return (
    <section>
      <motion.div
        initial={{ opacity: 0, y: 30 }}
        whileInView={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.8 }}
        viewport={{ once: true }}
        className='text-center mb-12'
      >
        <h2 className='text-3xl md:text-4xl font-bold mb-4 text-gray-900 dark:text-white'>
          Featured Blog
        </h2>
        <div className='w-24 h-1 bg-gradient-to-r from-primarycolor to-secondarycolor mx-auto mb-6'></div>
        <p className='text-lg text-gray-700 dark:text-gray-300 max-w-2xl mx-auto'>
          Discover our latest insights and updates from X-Space Real Estate
        </p>
      </motion.div>

      <motion.div
        initial={{ opacity: 0, y: 50 }}
        whileInView={{ opacity: 1, y: 0 }}
        transition={{ duration: 1 }}
        viewport={{ once: true }}
        className='grid grid-cols-1 lg:grid-cols-2 gap-8 items-center'
      >
        {/* Featured image */}
        <div className='relative overflow-hidden rounded-2xl shadow-xl h-[400px] lg:h-[500px]'>
          <Image
            src={featuredBlog.images?.[0] || '/placeholder.jpg'}
            alt={featuredBlog.title}
            fill
            className='object-cover transition-transform duration-700 hover:scale-110'
            style={{
              filter: loading ? 'blur(20px)' : 'none',
              transition: '1s filter linear',
            }}
            priority
            onLoad={() => setLoading(false)}
            sizes='(max-width: 768px) 100vw, (max-width: 1200px) 50vw, 40vw'
          />

          {/* Gradient overlay */}
          <div className='absolute inset-0 bg-gradient-to-t from-black/70 via-black/30 to-transparent'></div>

          {/* Category badge */}
          <div className='absolute top-4 left-4 bg-primarycolor text-white text-sm px-3 py-1 rounded-full'>
            {featuredBlog.category
              .split('_')
              .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
              .join(' ')}
          </div>
        </div>

        {/* Featured content */}
        <div className='space-y-6'>
          <span className='text-primarycolor dark:text-secondarycolor font-medium'>
            {featuredBlog.date}
          </span>

          <h3 className='text-3xl font-bold text-gray-900 dark:text-white'>
            {featuredBlog.title}
          </h3>

          <div className='text-gray-700 dark:text-gray-300 line-clamp-4'>
            {featuredBlog.description}
          </div>

          <Link rel='preload' href={`/blog/${featuredBlog.slug}`}>
            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              className='px-6 py-3 bg-primarycolor text-white rounded-lg hover:bg-secondarycolor transition-colors duration-300 flex items-center gap-2 mt-3'
            >
              Read Blog
              <svg
                xmlns='http://www.w3.org/2000/svg'
                width='20'
                height='20'
                viewBox='0 0 24 24'
                fill='none'
                stroke='currentColor'
                strokeWidth='2'
                strokeLinecap='round'
                strokeLinejoin='round'
              >
                <path d='M5 12h14'></path>
                <path d='m12 5 7 7-7 7'></path>
              </svg>
            </motion.button>
          </Link>
        </div>
      </motion.div>
    </section>
  );
}
